<template>
  <form-section
    :direction="direction"
    :error="error"
    :hint="hint"
    :label="label"
    :label-visible="labelVisible"
    :name="name"
  >
    <div
      :class="rowClasses"
      class="text-input__row"
    >
      <div
        v-if="hasPrefix"
        class="text-input__prefix"
      >
        {{ prefix }}
      </div>
      <input
        ref="input"
        :id="name"
        :name="name"
        :value="value"
        :type="type"
        :disabled="isDisabled"
        :maxlength="maxLength"
        :placeholder="placeholder"
        :autocomplete="autocompleteValue"
        class="text-input__input"
        @input="handleInputValue"
      >
      <div
        v-if="hasSuffix"
        class="text-input__suffix"
      >
        {{ suffix }}
      </div>
    </div>
    <button-input
      v-clipboard:copy="value"
      v-clipboard:success="handleCopy"
      v-clipboard:error="handleCopyError"
      v-if="isCopyable"
      class="text-input__copy"
    >
      <font-awesome-icon icon="clipboard" />
      <span v-t="'c.textInput.form.action.copyToClipboard'" />
    </button-input>
    <slot name="addon" />
  </form-section>
</template>

<script>
import isBlank from 'is-blank'
import Cleave from 'cleave.js'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import ButtonInput from '@/components/ui/ButtonInput'
import InputCommon from './InputCommon'

export default {
  name: 'TextInput',
  mixins: [InputCommon],
  components: {
    FontAwesomeIcon,
    ButtonInput
  },
  props: {
    labelVisible: {
      type: Boolean,
      default: () => true
    },
    type: {
      type: String,
      default: () => 'text'
    },
    value: {
      type: String,
      default: () => ''
    },
    isCopyable: {
      type: Boolean,
      default: () => false
    },
    prefix: {
      type: String,
      default: () => null
    },
    suffix: {
      type: String,
      default: () => null
    },
    maxLength: {
      type: Number,
      default: () => 1000
    },
    isAutocompleteEnabled: {
      type: Boolean,
      default: () => true
    },
    formatterOptions: {
      type: Object,
      default: () => null
    },
    formatterInput: {
      type: String,
      default: () => 'raw',
      validator (value) {
        return ['raw', 'formatted'].includes(value)
      }
    }
  },
  data () {
    return {
      formatter: null
    }
  },
  computed: {
    hasPrefix () {
      return !isBlank(this.prefix)
    },
    hasSuffix () {
      return !isBlank(this.suffix)
    },
    autocompleteValue () {
      return this.isAutocompleteEnabled ? 'on' : 'off'
    },
    rowClasses () {
      return {
        'text-input__row--has-prefix': this.hasPrefix,
        'text-input__row--has-suffix': this.hasSuffix
      }
    }
  },
  watch: {
    value () {
      this.setValue()
    }
  },
  methods: {
    setValue () {
      if (this.formatter) {
        this.formatter.setRawValue(this.value)
      } else {
        this.$refs.input.value = this.value
      }
    },
    updateValue (event) {
      let value = event.target.value
      if (this.formatter) {
        switch (this.formatterInput) {
          case 'raw':
            this.$emit('input', this.formatter.getRawValue())
            return
          case 'formatted':
            this.$emit('input', this.formatter.getFormattedValue())
            return
          default:
            throw new Error('[prop] `formatterInput` has to be `raw` or `formatted`')
        }
      }
      if (value !== event.target.value) {
        event.target.value = value
      }
      this.$emit('input', value)
    },
    createFormatter () {
      if (this.formatterOptions) {
        this.destroyFormatter()
        this.formatter = new Cleave(this.$refs.input, this.formatterOptions)
      }
    },
    destroyFormatter () {
      if (this.formatter) {
        this.formatter.destroy()
      }
    },
    handleCopy () {
      console.log('copied')
    },
    handleCopyError () {
      console.log('copy error')
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.createFormatter()
      this.setValue()
    })
  },
  updated () {
    this.createFormatter()
  },
  beforeDestroy () {
    this.destroyFormatter()
  }
}
</script>

<style lang="sass">
@import '~styles/imports'
@import '~styles/forms/mixins'

.text-input__input
  @extend %form-text-input-style

.text-input__row
  display: flex
  width: 100%

.text-input__prefix,
.text-input__suffix
  @include font-size(15, 48)
  background: $color-grey-1
  border: 1px solid $color-grey-1
  color: $color-text-base
  display: block
  font-family: $font-1
  font-weight: 600
  height: rem-calc(61)
  padding: rem-calc(6 20)
  transition: $global-transition

.text-input__prefix
  border-radius: 3px 0 0 3px

.text-input__suffix
  border-radius: 0 3px 3px 0

.text-input__row--has-prefix .text-input__input
  border-top-left-radius: 0
  border-bottom-left-radius: 0

.text-input__row--has-suffix .text-input__input
  border-top-right-radius: 0
  border-bottom-right-radius: 0

.form-section__column > .text-input__copy
  @include font-size(15, 48)
  border-bottom-left-radius: 0
  border-top-left-radius: 0
  bottom: 0
  height: rem-calc(61)
  line-height: 1
  position: absolute
  right: 0
  top: 0

</style>



// WEBPACK FOOTER //
// src/components/forms/TextInput.vue